// Teams, inzetbaarheid en persoonskoppelingen (backend: apps/planning/api_teams.py). Teams leest iedereen (ze
// groeperen het bord); perioden, Planner-resources en het logboek alleen de planner, en enkel die wijzigt.
// De meldingstekst komt van de server, gelijk aan het logboek; Ongedaan maken voert de tegenzet uit.
// Onderaan: de capaciteit uit Clockify zoals Teams, Nieuwe taak, Support en Tijdlijn ze tonen.
import { useMutation, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { useMemo } from "react";
import { call } from "./api";
import { loadTone, useCapacity, weekLoad, type Absence, type CapOptions, type Capacity, type CapPerson } from "./api-clockify";
import { addDays, ddmm, diffDays, isoWeek, mondayOf } from "./planning";
import { useSettings } from "./settings";
import { useToast } from "../shell/Toast";

export interface Team { id: number; name: string; sort: number; members: string[] }
/** Minder (of weer volledig) inzetbaar voor de planning: van maandag tot en met maandag, of doorlopend (to null). */
export interface InzetPeriod { id: number; personId: string; from: string; to: string | null; pct: number; kind: string; note: string; by: string; at: string }
export interface LogEntry { id: number; text: string; at: string; who: string }
export interface TeamsData { teams: Team[]; inzet: InzetPeriod[]; log: LogEntry[] }
export interface NewInzet { personId: string; from: string; to: string | null; pct: number; kind: string; note: string }

/** Planner-resource (bookableresource) met het Entra-account erachter; zonder account (generiek): personId null. */
export interface Resource { id: string; name: string; personId: string | null; account: string; guest: boolean; enabled: boolean }
/** Een gast- of tweede account (alias) telt als deze persoon. */
export interface PersonLink { aliasId: string; personId: string; by: string; at: string }
export interface LinksData { resources: Resource[]; links: PersonLink[]; log: LogEntry[] }

export interface WriteResult { ok: boolean; text: string; id?: number; changeId?: number }

export const teamsApi = {
  teams: () => call<TeamsData>("GET", "/api/teams"),
  createTeam: (name: string, members: string[]) => call<WriteResult>("POST", "/api/teams", { name, members }),
  updateTeam: (id: number, body: { name?: string; members?: string[] }) => call<WriteResult>("PUT", `/api/teams/${id}`, body),
  deleteTeam: (id: number) => call<WriteResult>("DELETE", `/api/teams/${id}`, {}),
  moveMember: (personId: string, teamId: number | null) => call<WriteResult>("PUT", `/api/team-members/${personId}`, { teamId }),
  addInzet: (p: NewInzet) => call<WriteResult>("POST", "/api/inzet", p),
  removeInzet: (id: number) => call<WriteResult>("DELETE", `/api/inzet/${id}`, {}),
  links: (fresh = false) => call<LinksData>("GET", "/api/person-links" + (fresh ? "?fresh=1" : "")),
  link: (aliasId: string, personId: string) => call<WriteResult>("POST", "/api/person-links", { aliasId, personId }),
  unlink: (aliasId: string) => call<WriteResult>("DELETE", `/api/person-links/${aliasId}`, {}),
};

export function useTeams() {
  return useQuery({ queryKey: ["teams"], queryFn: teamsApi.teams, staleTime: 60e3 });
}

export function usePersonLinks(enabled = true) {
  return useQuery({ queryKey: ["person-links"], queryFn: () => teamsApi.links(), staleTime: 60e3, enabled });
}

export interface Write {
  run: () => Promise<WriteResult>;
  /** de tegenzet, voor Ongedaan maken in de melding */
  undo?: (r: WriteResult) => Promise<unknown>;
  keys: QueryKey[];
}

/** Eén wijziging: uitvoeren, de gegevens opnieuw laden, en de melding van de server met Ongedaan maken. */
export function useTeamWrite() {
  const qc = useQueryClient();
  const toast = useToast();
  const refresh = (keys: QueryKey[]) => keys.forEach((k) => qc.invalidateQueries({ queryKey: k }));
  const m = useMutation({
    mutationFn: (w: Write) => w.run(),
    onSuccess: (r, w) => {
      refresh(w.keys);
      const undo = w.undo;
      toast.flash(r.text, undo ? {
        undo: () => undo(r).then(
          () => { refresh(w.keys); toast.flash("Ongedaan gemaakt"); },
          (e: Error) => { refresh(w.keys); toast.flash(e.message, { error: true }); },
        ),
      } : {});
    },
    onError: (e: Error) => toast.flash(e.message, { error: true }),
  });
  return { run: (w: Write) => m.mutateAsync(w), busy: m.isPending };
}

export const NO_TEAM = "Zonder team";
export const INZET_KINDS = ["Losstaand project", "Opleiding", "Intern werk", "Anders"];
export const INZET_PCTS = [100, 80, 60, 50, 40, 20, 0];

export interface Week { monday: string; label: string; sub: string }
/** Deze week en de volgende: per week de maandag, W41 en 05/10. */
export function weeksFrom(today: string, n = 8): Week[] {
  const m0 = mondayOf(today);
  return Array.from({ length: n }, (_, i) => { const m = addDays(m0, 7 * i); return { monday: m, label: "W" + isoWeek(m), sub: ddmm(m) }; });
}

/** De periode die in de week van deze maandag geldt: een nieuwere gaat voor op een oudere. */
export function periodAt(inzet: InzetPeriod[], personId: string, monday: string): InzetPeriod | null {
  let best: InzetPeriod | null = null;
  for (const e of inzet) if (e.personId === personId && e.from <= monday && (!e.to || e.to >= monday) && (!best || e.id > best.id)) best = e;
  return best;
}

/** "W41", "W41 tot W44" of "vanaf W41, doorlopend" */
export function periodText(e: { from: string; to: string | null }): string {
  const w = (s: string) => "W" + isoWeek(s);
  return !e.to ? "vanaf " + w(e.from) + ", doorlopend" : e.to === e.from ? w(e.from) : w(e.from) + " tot " + w(e.to);
}

/** Wie in welk team zit (iemand zit in hoogstens één team). */
export function teamOfPerson(teams: Team[] | undefined): Map<string, Team> {
  const m = new Map<string, Team>();
  for (const t of teams || []) for (const p of t.members) m.set(p, t);
  return m;
}

/** "vandaag 14:20" of "24/09 11:20" */
export function whenText(at: string, today: string): string {
  const d = new Date(at);
  const day = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Brussels", year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
  const hm = d.toLocaleTimeString("nl-BE", { timeZone: "Europe/Brussels", hour: "2-digit", minute: "2-digit" });
  return (day === today ? "vandaag" : ddmm(day)) + " " + hm;
}

export const nOf = (x: number, one: string, many: string) => x + " " + (x === 1 ? one : many);

// ---------- capaciteit uit Clockify ----------

/** De bezetting zoals de schermen ze standaard tonen: achterstand verdeeld over 4 weken, support meegeteld. */
export const LOAD_OPTS: CapOptions = { backlogWeeks: 4, withSupport: true };
export type Tone = ReturnType<typeof loadTone>;
/** Tekstkleur van een bezetting; enkel vol en overvol vallen op. */
export const TONE_CLS: Record<Tone, string> = { afwezig: "t-mute", ruimte: "t-ok", normaal: "t-ok", vol: "t-warn", overvol: "t-err" };

export interface CapView {
  cap: Capacity;
  /** wie in Clockify staat (op e-mailadres), per personId; wie er niet bij staat, toont het scherm zoals zonder Clockify */
  people: Map<string, CapPerson>;
  /** index in cap.weeks van de week van deze dag; -1 buiten de horizon */
  weekOf: (day: string) => number;
  /** bezetting in week i, standaard gerekend (LOAD_OPTS) */
  load: (p: CapPerson, i: number) => { load: number; cap: number; pct: number | null };
  /** kleur volgens de drempels in Instellingen */
  tone: (pct: number | null) => Tone;
  /** drempel overvol in % (Instellingen) */
  over: number;
  /** standaard werkweek uit Instellingen */
  weekHours: number;
}

/** Capaciteit voor een scherm, of null zolang ze laadt, als ze faalt of als Clockify uit staat: dan valt het scherm terug
 *  op wat het zonder Clockify toont. */
export function useCapacityView(enabled = true): CapView | null {
  const q = useCapacity(enabled);
  const { data: s } = useSettings();
  return useMemo(() => {
    const cap = q.data;
    if (!enabled || !cap || cap.clockify.state === "uit") return null;
    const t = { room: s?.settings.room ?? 50, full: s?.settings.full ?? 90, over: s?.settings.over ?? 100 };
    const idx = new Map(cap.weeks.map((w, i) => [w.monday, i]));
    return {
      cap,
      people: new Map(cap.people.filter((p) => p.clockifyUserId).map((p) => [p.personId, p])),
      weekOf: (day: string) => idx.get(mondayOf(day)) ?? -1,
      load: (p: CapPerson, i: number) => weekLoad(p, i, LOAD_OPTS),
      tone: (pct: number | null) => loadTone(pct, t),
      over: t.over,
      weekHours: s?.settings.weekHours ?? 40,
    };
  }, [q.data, s, enabled]);
}

/** Afwezigheden over de weken heen: aaneensluitend (ook over een weekend) wordt één periode. */
export function mergeAbsences(list: Absence[]): Absence[] {
  const out: Absence[] = [];
  for (const a of [...list].sort((x, y) => x.from.localeCompare(y.from))) {
    const last = out[out.length - 1];
    if (last && last.kind === a.kind && last.label === a.label && diffDays(last.to, a.from) <= 3) last.to = a.to > last.to ? a.to : last.to;
    else out.push({ ...a });
  }
  return out;
}

/** "12/10 tot 16/10" of "11/11" */
export const spanText = (a: { from: string; to: string }) => (a.from === a.to ? ddmm(a.from) : ddmm(a.from) + " tot " + ddmm(a.to));

/** "verlof 12/10 tot 16/10", "11/11 Wapenstilstand", "24/12 tot 31/12 sluiting"; het soort verlof tonen we niet */
export function absenceText(a: Absence): string {
  return a.kind === "verlof" ? "verlof " + spanText(a) : spanText(a) + " " + (a.label || (a.kind === "feestdag" ? "feestdag" : "sluiting"));
}

/** De afwezigheid op deze dag, als die er is. */
export function absentOn(cv: CapView, p: CapPerson, day: string): Absence | null {
  const i = cv.weekOf(day);
  return i < 0 ? null : p.weeks[i].absent.find((a) => a.from <= day && a.to >= day) || null;
}

/** Gewone werkweek van een persoon (zonder afwezigheden en inzetbaarheid), terug afgeleid uit de capaciteit. */
export function normalWeek(cv: CapView, p: CapPerson, pctOf: (monday: string) => number): number {
  const p0 = pctOf(cv.cap.weeks[0].monday);
  if (p0 > 0) return (p.weekHours * 100) / p0;
  const i = p.weeks.findIndex((w, j) => !w.absent.length && w.cap > 0 && pctOf(cv.cap.weeks[j].monday) > 0);
  return i >= 0 ? (p.weeks[i].cap * 100) / pctOf(cv.cap.weeks[i].monday) : cv.weekHours;
}
